import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { HeroIllo, AboutIllo } from "../components/AnimatedIllustrations";

const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const VALUES = [
  {
    title: "Engineering Excellence",
    desc: "We write code that survives the next engineer. Architecture decisions are made with scale and maintainability in mind — not just deadlines.",
    color: "border-violet-500/30 bg-violet-500/5",
    accent: "text-violet-400",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
  {
    title: "Product Mindset",
    desc: "We think like founders. Every feature we build is weighed against user value, business impact, and technical sustainability.",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    title: "Radical Transparency",
    desc: "No surprises. We communicate early, communicate often, and keep you in the loop with real builds — not status slides.",
    color: "border-emerald-500/30 bg-emerald-500/5",
    accent: "text-emerald-400",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
      </svg>
    ),
  },
  {
    title: "Long-term Partnership",
    desc: "We're not a dev shop that disappears after launch. We become embedded partners who care about your metrics as much as you do.",
    color: "border-amber-500/30 bg-amber-500/5",
    accent: "text-amber-400",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
      </svg>
    ),
  },
];

const TEAM_STATS = [
  { num: "2018", label: "Founded", sub: "Karachi, Pakistan · Texas, US" },
  { num: "40+", label: "Engineers", sub: "Full-time talent globally" },
  { num: "$50M+", label: "Revenue Generated", sub: "For our clients" },
  { num: "22+", label: "Industries Served", sub: "Across US, Canada & beyond" },
];

const PROCESS_STEPS = [
  {
    num: "01",
    title: "Discovery Call",
    desc: "We start with a deep-dive conversation about your business goals, technical challenges, and what success looks like. No templates — just real dialogue.",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25z" />
      </svg>
    ),
  },
  {
    num: "02",
    title: "Architecture & Scoping",
    desc: "Our engineers define the technical architecture, project scope, timeline, and budget before any contracts are signed. You get visibility, not surprises.",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
      </svg>
    ),
  },
  {
    num: "03",
    title: "Agile Build Sprints",
    desc: "Two-week sprints with real deployed builds every cycle. You review working software, not mockups. QA is built in, not bolted on.",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25z" />
      </svg>
    ),
  },
  {
    num: "04",
    title: "Launch & Beyond",
    desc: "Zero-downtime deployment, monitoring setup, and post-launch iteration. We stay the course until your metrics are green and your users are happy.",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
      </svg>
    ),
  },
];

export default function AboutPage() {
  useSEO({
    title: "About BerryByte | Software Dev Agency | US & Canada",
    description: "BerryByte Innovations — product-focused software dev agency with 40+ engineers. Serving US & Canadian startups and enterprises since 2018.",
    keywords: "about BerryByte Innovations, software development agency Texas, tech company US Canada, software engineering team, dedicated development agency",
    canonical: "https://www.berrybyteinn.com/about",
  });

  return (
    <SharedLayout>
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-violet-600 -top-32 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-cyan-700 bottom-0 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <HeroIllo>
          <AboutIllo />
        </HeroIllo>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-violet-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Home / About
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
              Founded 2018 · Texas, US
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              We're the team
              <br />
              <span className="bg-gradient-to-r from-violet-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                behind the product.
              </span>
            </h1>

            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl">
              BerryByte Innovations is a product-minded software studio. We architect, build, and scale digital products for startups and enterprises across the US & Canada. Not a dev shop — your embedded technical co-founders.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-16 bg-[#070b14] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {TEAM_STATS.map((stat) => (
              <div key={stat.label} className="text-center p-6 rounded-2xl border border-white/5 bg-white/[0.02]">
                <div className="text-4xl font-black text-white mb-1">{stat.num}</div>
                <div className="text-sm font-semibold text-slate-300">{stat.label}</div>
                <div className="text-xs text-slate-500 mt-1">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="relative py-28 bg-[#050810] overflow-hidden">
        <GlowOrb className="w-[600px] h-[600px] bg-violet-700 top-1/2 -translate-y-1/2 -left-48" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-violet-400 font-semibold text-sm tracking-widest uppercase">Our Story</span>
              <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white leading-tight">
                Engineering the future,
                <br />
                <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                  one commit at a time.
                </span>
              </h2>
              <p className="mt-6 text-slate-400 text-lg leading-relaxed">
                Founded in 2018 on the belief that great software is the ultimate competitive advantage, BerryByte Innovations brings together world-class engineers, designers, and product thinkers to build technology that scales.
              </p>
              <p className="mt-4 text-slate-400 text-lg leading-relaxed">
                We started as a small team of engineers who believed dev agencies were too focused on billing hours and not enough on outcomes. So we built the firm we always wished existed — one that obsesses over architecture, ships beautiful interfaces, and deploys infrastructure that doesn't break at 3AM.
              </p>
              <p className="mt-4 text-slate-400 text-lg leading-relaxed">
                Today we serve startups and enterprises across the US and Canada, with 40+ full-time engineers and a track record of $50M+ in client revenue generated.
              </p>
            </div>

            <div className="space-y-4">
              {[
                "Agile delivery with predictable timelines",
                "Transparent, async-first communication",
                "Code quality that survives the next engineer",
                "Post-launch support & continuous improvement",
                "US-based project management, global engineering talent",
                "98% client retention rate since 2018",
                "NDA-protected, SOC 2 compliant workflows",
                "Fixed-price and time-and-materials engagements",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.02] transition-colors">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
                    <Check />
                  </span>
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-28 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-cyan-700 -top-32 -right-48" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-semibold text-sm tracking-widest uppercase">What Drives Us</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">
              Our core values
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              These aren't wall posters. They're the principles that guide every decision we make.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className={`rounded-2xl border p-8 ${v.color} hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${v.accent} mb-5`}>
                    <Icon />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{v.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="relative py-28 bg-[#050810] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-400 font-semibold text-sm tracking-widest uppercase">How We Work</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">
              Our engagement model
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              From first call to production deployment, here's how we work with our clients.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS_STEPS.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.num} className="relative p-6 rounded-2xl border border-white/5 bg-white/[0.02] hover:-translate-y-1 transition-transform duration-300">
                  <div className="text-6xl font-black text-white/5 absolute top-4 right-4">{step.num}</div>
                  <div className="inline-flex p-3 rounded-xl bg-violet-500/10 border border-violet-500/20 text-violet-400 mb-4">
                    <Icon />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-violet-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">
            Ready to work together?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Tell us about your project. We respond within one business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold hover:from-violet-500 hover:to-cyan-500 hover:-translate-y-1 transition-all duration-300 shadow-2xl shadow-violet-900/40">
              Start a Project
            </Link>
            <a href="tel:+12018469830" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Call +1 201-846-9830
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
